/**
 * BookInfographicPage — standalone /infographic and /infographic/:bookId
 * page. Delegates rendering to <BookInfographicBody /> so the overlay
 * used inside /bodha and /sankalpa reuses the same fetch + view code.
 *
 * URL shape:
 *   /infographic                     — preview harness (book chooser)
 *   /infographic/:bookId             — single book infographic
 *   /infographic?book=<id>           — same as :bookId, query-param alt
 */
import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import Navigation from "../components/homev3/Navigation";
import Footer from "../components/homev3/Footer";
import ParchmentBackground from "../components/ParchmentBackground";
import BookInfographicBody from "../components/BookInfographicBody";
import { bookExamples } from "../data/bookExamples";

// Preview harness — a small chooser so the founder can flip through
// several books' layouts before greenlighting other languages.
function PreviewChooser() {
  const bucket = [4, 5, 7, 18, 50, 22, 30, 48].map((id) => {
    const b = bookExamples.find((x) => x.id === id);
    return b ? { id: String(b.id), title: b.title, author: b.author } : null;
  }).filter(Boolean);

  return (
    <div className="bic-chooser" data-testid="bic-preview-chooser">
      <p className="bic-chooser-eyebrow">INFOGRAPHIC PREVIEW HARNESS</p>
      <h1 className="bic-chooser-title">Flip through the layouts.</h1>
      <p className="bic-chooser-lead">
        A curated spread of eight books — five showcase, three with
        distinct tone. Open any one to see how the two-column parchment
        holds real content.
      </p>
      <ul className="bic-chooser-grid" data-testid="bic-chooser-grid">
        {bucket.map((b) => (
          <li key={b.id}>
            <Link
              to={`/infographic/${b.id}`}
              className="bic-chooser-card"
              data-testid={`bic-chooser-${b.id}`}
            >
              <span className="bic-chooser-num">{String(b.id).padStart(2, "0")}</span>
              <span className="bic-chooser-book">
                <strong>{b.title}</strong>
                <em>{b.author}</em>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Look up the frontend snapshot for a given book id so first-time
// visitors don't hit a 404. bookExamples ids are numeric.
function findSnapshot(bookId) {
  if (!bookId) return null;
  const b = bookExamples.find((x) => String(x.id) === String(bookId));
  if (!b) return null;
  return {
    id:          String(b.id),
    title:       b.title || "",
    author:      b.author || "",
    summary:     b.summary || "",
    insights:    Array.isArray(b.insights) ? b.insights : [],
    actionItems: Array.isArray(b.actionItems) ? b.actionItems : [],
    coreTheme:   b.coreTheme || "",
  };
}

export default function BookInfographicPage() {
  const params = useParams();
  const [search] = useSearchParams();
  const bookId = params.bookId || search.get("book") || null;
  const langParam = (search.get("lang") || "en").toLowerCase();
  const snapshot = findSnapshot(bookId);

  return (
    <div className="min-h-screen flex flex-col" data-testid="book-infographic-page">
      <ParchmentBackground />
      <Navigation />
      <main className="flex-1 w-full" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div className="bic-container">
          {!bookId && <PreviewChooser />}
          {bookId && (
            <>
              <BookInfographicBody
                bookId={bookId}
                bookSnapshot={snapshot}
                lang={langParam}
                backHref="/infographic"
              />
              <div className="bic-nav">
                <Link to="/infographic" className="bic-nav-back" data-testid="bic-back-to-chooser">
                  ← Back to preview harness
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
